import { useMemo } from 'react';
import ReactMarkdown, { uriTransformer } from 'react-markdown';
import { string } from 'prop-types';

import LinkInternal from '~/components/LinkInternal';

import { INTERNAL_PATH } from '~/constants/urls';

/**
 * when list in Markdown text is written in a format:
 *
 * `1. lorem ipsum\n\n2.lorem ipsum 2\n\n`
 *
 * the Markdown parser places the additional `\n` INSIDE the li tag and wraps the text with a p tag
 * for example the above text is parsed would be equivalent to this JSX expression:
 *
 * ```
 * <ol>
 *  <li>{`/n`}<p>lorem ipsum</p>{`\n`}</li>
 *  <li>{`/n`}<p>lorem ipsum 2</p>{`\n`}</li>
 * </ol>
 * ```
 *
 * the component below is used to remove the trailing {`\n`} elements from the children array of a li element
 */
function Li(liProps) {
  const { children: propsChildren, ...props } = liProps;

  const children = useMemo(() => {
    if (!Array.isArray(propsChildren) || propsChildren.length <= 1) {
      return propsChildren;
    }
    const trimmedProps = propsChildren;
    if (trimmedProps[0] === '\n') {
      trimmedProps.splice(0, 1);
    }
    if (trimmedProps[trimmedProps.length - 1] === '\n') {
      trimmedProps.splice(-1, 1);
    }
    return trimmedProps;
  }, [propsChildren]);

  return <li {...props}>{children}</li>;
}

const components = {
  a: (props) => <LinkInternal {...props} />,
  // I'm begging for a markdown rewrite
  li: ({ node: _, ...props }) => <Li {...props} />,
};

const transformLinkUri = (uri) =>
  uri.startsWith(INTERNAL_PATH) ? uri : uriTransformer(uri);

const newLineSpaceRegex = /\n /g;

export default function Markdown({ children, ...markdownProps }) {
  const markdownWithLineBreaks = useMemo(() => {
    return typeof children === 'string' && children.replace
      ? children.replace(newLineSpaceRegex, '\n&nbsp;')
      : children;
  }, [children]);

  return (
    <ReactMarkdown
      {...markdownProps}
      components={components}
      transformLinkUri={transformLinkUri}
    >
      {markdownWithLineBreaks}
    </ReactMarkdown>
  );
}

Markdown.propTypes = {
  children: string,
  className: string,
};
